/**
 * Gravatar helpers.
 *
 * Gravatar accepts a SHA-256 hex digest of the lowercased, trimmed email
 * (modern replacement for the legacy MD5 scheme — no extra dependency needed,
 * SHA-256 is available via the Web Crypto API). We request `d=404` so the
 * image request fails when the user has no Gravatar, letting the caller fall
 * back to another avatar source via an `onError` handler.
 */

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Build a Gravatar URL for an email. Returns null if the email is empty or
 * the Web Crypto API is unavailable. Uses `d=404` so a missing avatar 404s.
 */
export async function getGravatarUrl(
  email: string | null | undefined,
  size = 200
): Promise<string | null> {
  if (!email || !crypto?.subtle) return null;
  const normalized = email.trim().toLowerCase();
  if (!normalized) return null;
  const hash = await sha256Hex(normalized);
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=404`;
}
